import { useState, useEffect } from 'react'
import { Loader2, Calendar, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import type { Schedule, ScheduleCreate, ScheduleUpdate, Site } from '@/types'

const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

const toneOptions = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'friendly', label: 'Friendly' },
]

interface ScheduleModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedule?: Schedule | null
  sites: Site[]
  onSubmit: (data: ScheduleCreate | ScheduleUpdate) => Promise<void>
  isSubmitting: boolean
}

export function ScheduleModal({
  open,
  onOpenChange,
  schedule,
  sites,
  onSubmit,
  isSubmitting,
}: ScheduleModalProps) {
  const isEditing = !!schedule

  const [formData, setFormData] = useState<ScheduleCreate>({
    name: '',
    description: '',
    site_id: '',
    topic_template: '',
    keywords: [],
    target_word_count: 1500,
    tone: 'professional',
    language: 'en',
    include_image: true,
    frequency: 'daily',
    hour: 9,
    minute: 0,
    day_of_week: 0,
    day_of_month: 1,
    timezone: 'America/New_York',
    articles_per_run: 1,
    max_articles: undefined,
    auto_publish: false,
    publish_delay_minutes: 0,
  })
  const [keywordInput, setKeywordInput] = useState('')

  useEffect(() => {
    if (schedule) {
      setFormData({
        name: schedule.name,
        description: schedule.description || '',
        site_id: schedule.site_id,
        topic_template: schedule.topic_template,
        keywords: schedule.keywords,
        target_word_count: schedule.target_word_count,
        tone: schedule.tone,
        language: schedule.language as 'en' | 'he',
        include_image: schedule.include_image,
        frequency: schedule.frequency,
        hour: schedule.hour,
        minute: schedule.minute,
        day_of_week: schedule.day_of_week ?? 0,
        day_of_month: schedule.day_of_month ?? 1,
        timezone: schedule.timezone,
        articles_per_run: schedule.articles_per_run,
        max_articles: schedule.max_articles ?? undefined,
        auto_publish: schedule.auto_publish,
        publish_delay_minutes: schedule.publish_delay_minutes,
      })
    } else {
      setFormData({
        name: '',
        description: '',
        site_id: sites[0]?.id || '',
        topic_template: '',
        keywords: [],
        target_word_count: 1500,
        tone: 'professional',
        language: 'en',
        include_image: true,
        frequency: 'daily',
        hour: 9,
        minute: 0,
        day_of_week: 0,
        day_of_month: 1,
        timezone: 'America/New_York',
        articles_per_run: 1,
        max_articles: undefined,
        auto_publish: false,
        publish_delay_minutes: 0,
      })
    }
    setKeywordInput('')
  }, [schedule, sites, open])

  const handleAddKeyword = () => {
    const keyword = keywordInput.trim().toLowerCase()
    if (keyword && !formData.keywords?.includes(keyword)) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...(prev.keywords || []), keyword],
      }))
      setKeywordInput('')
    }
  }

  const handleRemoveKeyword = (keyword: string) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords?.filter((k) => k !== keyword) || [],
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {isEditing ? 'Edit Schedule' : 'Create New Schedule'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the schedule settings below'
              : 'Set up automated article generation on a schedule'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Schedule Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Weekly Blog Posts"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="site">Target Site *</Label>
              <Select
                value={formData.site_id}
                onValueChange={(value: string) => setFormData((prev) => ({ ...prev, site_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a site..." />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Brief description of this schedule"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic">Topic Template *</Label>
            <Input
              id="topic"
              placeholder="e.g., Best {weekday} tips for productivity"
              value={formData.topic_template}
              onChange={(e) => setFormData((prev) => ({ ...prev, topic_template: e.target.value }))}
              required
            />
            <p className="text-xs text-muted-foreground">
              Variables: {'{date}'}, {'{weekday}'}, {'{month}'}, {'{year}'}, {'{counter}'}
            </p>
          </div>

          {/* Keywords */}
          <div className="space-y-2">
            <Label htmlFor="keywords">Keywords</Label>
            <div className="flex gap-2">
              <Input
                id="keywords"
                placeholder="Add keyword..."
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddKeyword()
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={handleAddKeyword}>
                Add
              </Button>
            </div>
            {formData.keywords && formData.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-secondary rounded-md text-sm"
                  >
                    {keyword}
                    <button type="button" onClick={() => handleRemoveKeyword(keyword)}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Scheduling */}
          <div className="space-y-4">
            <h4 className="font-medium">Schedule Timing</h4>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value: string) =>
                    setFormData((prev) => ({ ...prev, frequency: value as ScheduleCreate['frequency'] }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">Once</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Hour (0-23)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={formData.hour}
                  onChange={(e) => setFormData((prev) => ({ ...prev, hour: parseInt(e.target.value) || 0 }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Minute (0-59)</Label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  value={formData.minute}
                  onChange={(e) => setFormData((prev) => ({ ...prev, minute: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            {formData.frequency === 'weekly' && (
              <div className="space-y-2">
                <Label>Day of Week</Label>
                <Select
                  value={String(formData.day_of_week ?? 0)}
                  onValueChange={(value: string) => setFormData((prev) => ({ ...prev, day_of_week: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dayNames.map((day, i) => (
                      <SelectItem key={day} value={String(i)}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.frequency === 'monthly' && (
              <div className="space-y-2">
                <Label>Day of Month (1-31)</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={formData.day_of_month ?? 1}
                  onChange={(e) => setFormData((prev) => ({ ...prev, day_of_month: parseInt(e.target.value) || 1 }))}
                />
              </div>
            )}
          </div>

          {/* Content Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Content Settings</h4>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Articles per Run</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={formData.articles_per_run}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, articles_per_run: parseInt(e.target.value) || 1 }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Tone</Label>
                <Select
                  value={formData.tone}
                  onValueChange={(value: string) => setFormData((prev) => ({ ...prev, tone: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {toneOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Language</Label>
                <Select
                  value={formData.language}
                  onValueChange={(value: string) => setFormData((prev) => ({ ...prev, language: value as 'en' | 'he' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="he">Hebrew</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Target Word Count: {formData.target_word_count}</Label>
              <Slider
                value={[formData.target_word_count || 1500]}
                onValueChange={([value]: number[]) => setFormData((prev) => ({ ...prev, target_word_count: value }))}
                min={500}
                max={5000}
                step={100}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>500</span>
                <span>5000</span>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h4 className="font-medium">Options</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Generate Featured Image</Label>
                  <p className="text-xs text-muted-foreground">AI-generated image for each article</p>
                </div>
                <Switch
                  checked={formData.include_image}
                  onCheckedChange={(checked: boolean) => setFormData((prev) => ({ ...prev, include_image: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Publish to WordPress</Label>
                  <p className="text-xs text-muted-foreground">Automatically publish when ready</p>
                </div>
                <Switch
                  checked={formData.auto_publish}
                  onCheckedChange={(checked: boolean) => setFormData((prev) => ({ ...prev, auto_publish: checked }))}
                />
              </div>

              {formData.auto_publish && (
                <div className="space-y-2 pl-4 border-l-2 border-muted">
                  <Label>Publish Delay (minutes)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={1440}
                    value={formData.publish_delay_minutes}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, publish_delay_minutes: parseInt(e.target.value) || 0 }))
                    }
                    className="w-32"
                  />
                  <p className="text-xs text-muted-foreground">Delay before auto-publishing (0 = immediate)</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Save Changes' : 'Create Schedule'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
